import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import type { WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { describeRejection } from "@/modules/settings/errors";

/**
 * "New group" (FR-ACL-6, M15-3).
 *
 * Members and roles are not asked here — a group with neither is still a
 * name worth reserving, and both are edited from `GroupDetail` right after,
 * the same two-step `InviteUserSheet` avoids only because a user needs at
 * least one role to sign in usefully. A `Sheet` for the same reason that one
 * is: it is the primitive `RecordDrawer` already opens over the list.
 */
export function NewGroupSheet({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (groupId: string) => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  function reset() {
    setName("");
    setDescription("");
    setRejection(null);
  }

  const createMutation = useMutation({
    mutationFn: () =>
      data.settings.createGroup(name.trim(), description.trim() || null),
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      const groupId = result.data.id;
      reset();
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["settings", "groups"] });
      onCreated(groupId);
    },
  });

  const fieldError = (field: string): string | undefined =>
    rejection?.kind === "invalid" ? rejection.fields?.[field] : undefined;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <SheetContent
        title="New group"
        description="Add members and roles once it exists."
        actions={<FeedbackButton />}
      >
        <form
          className="flex h-full flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            setRejection(null);
            createMutation.mutate();
          }}
        >
          <div className="flex-1 space-y-4 overflow-auto p-4 sm:p-6">
            <div className="space-y-1.5">
              <Label htmlFor="group-name">Name</Label>
              <Input
                id="group-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                required
              />
              {fieldError("name") ? (
                <p className="text-destructive text-xs">{fieldError("name")}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="group-description">Description</Label>
              <Textarea
                id="group-description"
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                }}
                rows={3}
              />
            </div>

            {rejection && rejection.kind !== "invalid" ? (
              <p className="text-destructive text-sm">
                {describeRejection(rejection)}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t p-4 sm:px-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || name.trim() === ""}
            >
              {createMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              Create group
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
